import { useRef, useState } from 'react'
import type { Locale } from '../content/site'

type OfficeImage = {
  src: string
  alt: string
  caption: string
}

type OfficeGalleryProps = {
  locale: Locale
  title: string
  viewFullImage: string
  images: OfficeImage[]
}

/** A single, responsive image viewport with accessible keyboard and touch controls. */
export function OfficeGallery({ locale, title, viewFullImage, images }: OfficeGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const touchStartX = useRef<number | null>(null)
  const count = images.length
  const showControls = count > 1

  function goTo(index: number) {
    setActiveIndex((index + count) % count)
  }

  function onTouchEnd(endX: number) {
    if (touchStartX.current === null) return
    const delta = endX - touchStartX.current
    touchStartX.current = null
    if (Math.abs(delta) < 45) return
    goTo(activeIndex + (delta < 0 ? 1 : -1))
  }

  if (count === 0) return null

  const labels = locale === 'fa'
    ? { previous: 'تصویر قبلی', next: 'تصویر بعدی', image: 'تصویر' }
    : { previous: 'Previous image', next: 'Next image', image: 'Image' }

  return (
    <section className="company-about__gallery" aria-label={title} aria-roledescription="carousel">
      <div
        className="company-about__gallery-viewport"
        dir="ltr"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'ArrowLeft') { event.preventDefault(); goTo(activeIndex - 1) }
          if (event.key === 'ArrowRight') { event.preventDefault(); goTo(activeIndex + 1) }
        }}
        onTouchStart={(event) => { touchStartX.current = event.touches[0]?.clientX ?? null }}
        onTouchEnd={(event) => { onTouchEnd(event.changedTouches[0]?.clientX ?? 0) }}
        onTouchCancel={() => { touchStartX.current = null }}
      >
        <div
          className="company-about__gallery-track"
          style={{ transform: `translate3d(-${activeIndex * 100}%, 0, 0)` }}
        >
          {images.map((image, index) => (
            <div
              className="company-about__gallery-slide"
              key={image.src}
              aria-hidden={index !== activeIndex}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} / ${count}`}
            >
              <a
                className="company-about__gallery-link"
                href={image.src}
                target="_blank"
                rel="noopener noreferrer"
                tabIndex={index === activeIndex ? 0 : -1}
                aria-label={`${image.alt} — ${viewFullImage}`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  width="1200"
                  height="900"
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  draggable={false}
                />
                <span className="company-about__gallery-open" aria-hidden="true">↗</span>
              </a>
            </div>
          ))}
        </div>

        {showControls && (
          <div className="company-about__gallery-arrows" aria-label={title}>
            <button
              className="company-about__gallery-arrow"
              type="button"
              aria-label={labels.previous}
              onClick={() => goTo(activeIndex - 1)}
            >
              <span aria-hidden="true">‹</span>
            </button>
            <button
              className="company-about__gallery-arrow"
              type="button"
              aria-label={labels.next}
              onClick={() => goTo(activeIndex + 1)}
            >
              <span aria-hidden="true">›</span>
            </button>
          </div>
        )}
      </div>

      <div className="company-about__gallery-footer" dir={locale === 'fa' ? 'rtl' : 'ltr'}>
        <div className="company-about__gallery-caption" aria-live="polite" aria-atomic="true">
          <span className="company-about__gallery-counter" dir="ltr">
            {String(activeIndex + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
          </span>
          <span>{images[activeIndex].caption}</span>
        </div>
        {showControls && (
          <div className="company-about__gallery-pagination" aria-label={title}>
            {images.map((image, index) => (
              <button
                key={image.src}
                type="button"
                className={`company-about__gallery-dot${index === activeIndex ? ' is-active' : ''}`}
                onClick={() => goTo(index)}
                aria-label={`${labels.image} ${index + 1}: ${image.caption}`}
                aria-pressed={index === activeIndex}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
